import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { InputForm } from '../../models/form/input-form';
import { VariantIconComponent } from '../variant-icon.component';

@Component({
  selector: 'app-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [formGroup]="group()" class="w-full">
      @if(inputForm().label && inputForm()!.label!.length > 0){
      <span
        [class]="inputForm().labelColor || 'text-header'"
        [ngClass]="{
          'text-red-700':
            (this.elementForm()!.hasError('required') ||
              this.elementForm()!.hasError('pattern')) &&
            submitted()
        }"
        >{{ inputForm().label }}:</span
      >
      }
      <div [formGroupName]="inputForm().groupFormName" class="flex flex-col">
        <div class="flex justify-center">
          @if(inputForm().labelSx && inputForm()!.labelSx!.length > 0) {
          <span
            [ngClass]="{
              'text-red-700':
                (this.elementForm()!.hasError('required') ||
                  this.elementForm()!.hasError('pattern')) &&
                submitted(),
              'w-1/3': this.inputForm().labelSx!.length > 3,
              'w-1/5': this.inputForm().labelSx!.length <= 3
            }"
            class="text-nowrap inline-flex items-end justify-start"
          >
            {{ inputForm().labelSx }}:
          </span>
          }
          <div class="w-full flex gap-1">
            <input
              formControlName="input"
              [type]="inputForm().type || 'text'"
              [placeholder]="inputForm().placeholder || ''"
              [class]="setClasses()"
              [ngClass]="{
                'input-error': (this.elementForm()!.hasError('required') || this.elementForm()!.hasError('pattern')) && submitted(),
              }"
            />
            @if(inputForm().buttonIcon) {
            <button
              class="tooltip"
              [attr.data-tip]="inputForm().buttonIcon?.tooltip"
            >
              <app-variant-icon
                [variant]="(inputForm().buttonIcon?.icon)!.variant"
                [class]="inputForm().buttonIcon?.icon?.class"
                (click)="submitButtonIcon.emit()"
              ></app-variant-icon>
            </button>
            }
          </div>
        </div>
        <div class="h-1">
          @if (submitted() && this.elementForm()!.hasError('required')) {
          <span class="text-red-700 text-sm align-top">Campo Obbligatorio</span>
          } @if (submitted() && !this.elementForm()!.hasError('required') &&
          this.elementForm()!.hasError('pattern') && inputForm().type ===
          'email') {
          <span class="text-red-700 text-sm align-top"
            >Inserisci una mail valida</span
          >
          } @if (submitted() && !this.elementForm()!.hasError('required') &&
          this.elementForm()!.hasError('pattern') && inputForm().type ===
          'password') {
          <span class="text-red-700 text-sm w-[20%] align-top"
            >La password deve essere composta da almeno un numero, una
            minuscola, una maiuscola e non deve contenere caratteri
            speciali</span
          >
          } @if (submitted() && this.elementForm()!.hasError('pattern') &&
          inputForm().type === 'number') {
          <span class="text-red-700 text-sm w-[20%] align-top">
            Inserire un numero decimale valido (es. 0.10)
          </span>
          }
        </div>
      </div>
    </div>
  `,
  styles: [``],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    VariantIconComponent,
  ],
})
export class InputComponent implements OnInit {
  group = input.required<FormGroup>();
  submitted = input<boolean>();
  inputForm = input.required<InputForm>();
  @Output() submitButtonIcon = new EventEmitter();
  elementForm = signal<AbstractControl<any, any> | null>(null);
  validators: Validators[] = [
    [Validators.required],
    [Validators.required, Validators.pattern(new RegExp(/\w+@\w+\.\w{2,4}/i))],
    [
      Validators.required,
      Validators.pattern(
        '(?=.*[A-Z])(?=.*[A-Za-z])(?=.*[0-9])(?=.*[$@$!#^~%*?&,.<>"\'\\;:{\\}\\[\\]\\|\\+\\-\\=\\_\\)\\(\\)\\`\\/\\\\\\]])[A-Za-z0-9d$@].{7,}'
      ),
    ],
    [
      Validators.pattern(
        '(?=.*[A-Z])(?=.*[A-Za-z])(?=.*[0-9])(?=.*[$@$!#^~%*?&,.<>"\'\\;:{\\}\\[\\]\\|\\+\\-\\=\\_\\)\\(\\)\\`\\/\\\\\\]])[A-Za-z0-9d$@].{7,}'
      ),
    ],
    [Validators.pattern(/^\d+\.\d+$/)],
    [Validators.required, Validators.pattern(/^\d+\.\d+$/)],
  ];

  setClasses = computed(
    () =>
      `input input-bordered placeholder:text-header w-full disabled:text-white disabled:bg-neutral-500 disabled:opacity-50 bg-slate-50/50 focus-within:bg-white transition-colors text-header ${
        this.inputForm()?.classesAdd
      }`
  );

  constructor(private formBuilder: FormBuilder) {
    effect(
      () => {
        this.group()
          .get(`${this.inputForm().groupFormName}`)!
          .get('input')
          ?.setValue(this.inputForm().select);
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.group().addControl(
      this.inputForm().groupFormName,
      this.formBuilder.group({
        input: [
          {
            value: this.inputForm().select,
            disabled: this.inputForm().disabledInput,
          },
          this.validators[this.inputForm().validatorNumber!],
        ],
      })
    );
    this.elementForm.set(
      this.group().get(`${this.inputForm().groupFormName}`)!.get('input')
    );
    this.group()
      .get(`${this.inputForm().groupFormName}`)!
      .get('input')!
      .updateValueAndValidity();
  }
}
